/**
 * LA Question Form
 * Note: js/la_questionform.js and js/la_questionform_v2.js is deprecated but we have to keep both in sync until we switch over.
 * ssm/feedbackWidget.js also has a version of the QuestionForm js that could need updating for anything changed here.
 */
import FileDrop from '../tools/FileDrop.js';
import { createAltchaWidget, Pbkdf2Worker } from '../tools/altchaCaptcha.js';

// see QueueConstants
const AUTOREPLY_TYPE_INACTIVE = 0;
const AUTOREPLY_TYPE_ALWAYS = 1;
const AUTOREPLY_TYPE_OFFHOURS = 2;
const AUTOREPLY_TYPE_SCHEDULED = 3;

/**
 * Auto reply settings for deciding if captcha should be shown
 */
class FormAutoReply {
    /**
     * @prop {Number} type Type of autoreply (see constants)
     * @prop {Number|null} start schedule start date in unix seconds
     * @prop {Number|null} end schedule end date in unix seconds
     * @prop {Array<Object{day: int, start: int, end: int}>} offhours
     * @prop {String} timezone
     * @prop {Boolean} libauth_enabled Is Libauth on for the form
     */
    constructor({
        type = 0,
        start = null,
        end = null,
        offhours = null,
        timezone = '',
        captchaAlwaysOn = false,
        libauth_enabled = false,
    }) {
        this.type = type;
        this.start = start;
        this.end = end;
        this.offhours = offhours;
        this.timezone = timezone;
        this.captchaAlwaysOn = captchaAlwaysOn;
        this.libauth_enabled = libauth_enabled;
    }

    /**
     * Should we enable the captcha because of the autoreply.
     * @returns {Boolean}
     */
    isCaptchaEnabled() {
        if (this.libauth_enabled) {
            return false;
        }
        if (this.captchaAlwaysOn) {
            return true;
        }
        return this.isAutoReplyEnabled();
    }

    /**
     * Is auto-reply enabled.
     * @returns {Boolean}
     */
    isAutoReplyEnabled() {
        if (this.type === AUTOREPLY_TYPE_INACTIVE) {
            return false;
        }
        if (this.type === AUTOREPLY_TYPE_ALWAYS) {
            return true;
        }
        if (this.type === AUTOREPLY_TYPE_SCHEDULED) {
            return this._isBetweenDates();
        }
        if (this.type === AUTOREPLY_TYPE_OFFHOURS) {
            return this._isNowOffHours();
        }
        return false;
    }

    /**
     * Get auto reply type
     * @returns {Number}
     */
    getAutoReplyType() {
        return this.type;
    }

    /**
     * Is now between the start/end timestamps.
     * @returns {Boolean}
     */
    _isBetweenDates() {
        if (!this.end || !this.start) {
            return false;
        }
        // calculate now compared to start/end.
        // milliseconds to unix seconds
        const unixTime = Math.floor(Date.now() / 1000);
        return (unixTime >= this.start
            && unixTime <= this.end);
    }

    _isNowOffHours() {
        if (!this.offhours || !this.timezone) {
            return false;
        }
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: this.timezone,
            weekday: 'short',
            hour: 'numeric',
            hour12: false,
        }).formatToParts(new Date());
        const weekdayMap = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
        const weekday = parts.find((p) => p.type === 'weekday')?.value;
        const hour = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
        const day = weekdayMap[weekday] ?? -1;
        const range = Object.values(this.offhours).find((r) => r.day === day);
        if (!range) {
            return false;
        }
        return !this._isBetweenHours(range.start, range.end, hour);
    }

    _isBetweenHours(start, end, hour) {
        if (start === 24 && end === 24) return false;
        if (start === end) return true;
        if (hour === start) return true;
        if (start > end) return !(hour < start && hour > end);
        return hour > start && hour < end;
    }
}

// see QuestionFormConstants
const ruleConstants = Object.freeze({
    RULE_OPERATOR_EQUALS: 0,
    RULE_OPERATOR_NOTEQUALS: 1,
    RULE_OPERATOR_INCLUDES: 2,
    RULE_OPERATOR_INCLUDES_ALL: 3,
    RULE_ACTION_SHOW: 0,
    RULE_ACTION_HIDE: 1,
    RULE_ACTION_NONE: -1,
});

/**
 * Field Rules
 */
class FormFieldRule {
    /**
     * @prop {Number} field1 Field which can trigger changes
     * @prop {Number} operator Constant for = !=
     * @prop {Array<Number>} value Field 1's value that triggers change
     * @prop {Number} field2 Field that is effected
     * @prop {Number} action Constant for show/hide
     */
    constructor({
        field1 = 0,
        operator = ruleConstants.RULE_OPERATOR_EQUALS,
        value = [],
        field2 = 0,
        action = ruleConstants.RULE_ACTION_SHOW,
    }) {
        this.field1 = field1;
        this.operator = operator;
        this.value = Array.isArray(value) ? value : [];
        this.field2 = field2;
        this.action = action;
    }

    get oppositeAction() {
        return this.action === ruleConstants.RULE_ACTION_SHOW
            ? ruleConstants.RULE_ACTION_HIDE
            : ruleConstants.RULE_ACTION_SHOW;
    }

    /**
     * Based on value of field do we hide/show/nothing.
     * @param {Number|Array<Number>} field2value Current field value.
     * @returns {Number}
     */
    getFieldAction(field2value) {
        // if field is reset to no answer then hide
        if (field2value === 0 || field2value.length === 0) {
            return ruleConstants.RULE_ACTION_HIDE;
        }
        switch (this.operator) {
            case ruleConstants.RULE_OPERATOR_EQUALS:
                return this.value.includes(field2value)
                    ? this.action
                    : this.oppositeAction;
            case ruleConstants.RULE_OPERATOR_NOTEQUALS:
                return !this.value.includes(field2value)
                    ? this.action
                    : this.oppositeAction;
            case ruleConstants.RULE_OPERATOR_INCLUDES:
                if (!Array.isArray(field2value)) {
                    field2value = [field2value];
                }
                // At least one must match
                return field2value.filter((el) => {
                    return this.value.includes(el);
                }).length > 0
                    ? this.action
                    : this.oppositeAction;
            case ruleConstants.RULE_OPERATOR_INCLUDES_ALL:
                if (!Array.isArray(field2value)) {
                    return ruleConstants.RULE_ACTION_NONE;
                }
                // All must match
                return field2value.filter((el) => {
                    return this.value.includes(el);
                }).length === this.value.length
                    ? this.action
                    : this.oppositeAction;
        }
        return ruleConstants.RULE_ACTION_NONE;
    }
}

class QuestionForm {
    /**
     * Question form js
     * @param {String} form_action Action attribute for the form, set it in js to help with spam??
     * @param {String} content_id box_content.id value
     * @param {Number} queue_id Form's Queue ID
     * @param {Object} errormsg Error message translation object
     * @param {Object} translations Translations for the filedrop widget.
     * @param {Boolean} libauth_enabled Is Libauth Turned on for this form.
     * @param {Boolean} libauth_authed Is the requester validated for libauth.
     * @param {FormAutoReply} autoReplySettings Queue auto reply type, etc.
     * @param {Array<FormFieldRule>} rules Field rules
     */
    constructor({
        form_action = '',
        content_id = 0,
        queue_id = 0,
        errormsg = {},
        translations = {},
        libauth_authed = false,
        libauth_enabled = false,
        autoReplySettings = {},
        rules = [],
    }) {
        this.form_action = form_action;
        this.divselector = `#s-la-askform-${content_id}`;
        this.queue_id = Number(queue_id);
        this.errormsg = errormsg;
        this.translations = translations;
        this.libauth_authed = !!libauth_authed;
        this.libauth_enabled = !!libauth_enabled;
        this.autoReplySettings = (autoReplySettings instanceof FormAutoReply)
            ? autoReplySettings
            : new FormAutoReply(autoReplySettings);
        this.successCallback = null;
        this.failureCallback = null;
        if (Array.isArray(rules)) {
            this.rules = rules.map((obj) => new FormFieldRule(obj));
        }
    }

    /**
     * Success callback gets called with the response from the ajax request
     * @param {Function} callback Called on successful ticket submission.
     */
    setSuccessCallback(callback) {
        if (typeof callback === 'function') {
            this.successCallback = callback;
        }
    }

    setFailureCallback(callback) {
        if (typeof callback === 'function') {
            this.failureCallback = callback;
        }
    }

    /**
     * When a required checkbox is changed we have to adjust the required attribute
     * on all of them else the browser will not validate
     * @param {Event} ev Checkbox input change.
     */
    handleRequiredCheckBoxes(ev) {
        const fieldName = ev.target.name;
        const isChecked = (ev.target.checked === true);
        // if checked remove required from related inputs.
        if (isChecked) {
            const relatedChecks = this.form.querySelectorAll(`input[type=checkbox][name="${fieldName}"]`);
            if (relatedChecks.length > 0) {
                relatedChecks.forEach((el) => {
                    el.removeAttribute('required');
                });
            }
            return;
        }
        // if unchecked confirm if others are checked.
        const fieldSet = ev.target.closest('fieldset');
        if (fieldSet === null) {
            return;
        }
        // Get checked boxes (if any)
        const checkedBoxes = fieldSet.querySelectorAll('input[type=checkbox]:checked');
        if (checkedBoxes.length > 0) {
            // Others are checked so all good.
            return;
        }
        // unchecked
        fieldSet.querySelectorAll('input[type=checkbox]').forEach((el) => {
            el.setAttribute('required', '');
        });
    }

    /**
     * Add error on the form
     * @param {HTMLElement} input Element for input/select
     * @param {String} msg Error message
     */
    markError(input, msg) {
        if (!input) {
            return;
        }
        const msg_div = document.createElement('div');
        msg_div.classList.add('error-message');
        msg_div.innerHTML = msg;
        const parent = input.closest('div.form-group');
        if (parent === null) {
            return;
        }
        input.setAttribute('aria-invalid', 'true');
        parent.classList.add('has-error');
        const label = parent.querySelector('label');
        if (label) {
            label.appendChild(msg_div);
        }
    }

    loadCaptcha() {
        // if action is relative fallback to current domain
        const url = new URL(this.form_action, `https://${window.location.hostname}`);
        createAltchaWidget(this.captchaDiv, {
            host: url.hostname,
            // This is necessary in case the webworker can't load
            // like if CSP blocks blob as a worker-src or script-src
            onError: () => this.loadFallbackCaptcha(),
        });
        this.captchaDiv.classList.add('active');
        this.captchaDiv.classList.remove('hidden');
    }

    loadFallbackCaptcha() {
        const url = new URL(this.form_action, `https://${window.location.hostname}`);
        const params = new URLSearchParams({ bs5: 1 });
        fetch(
            `https://${url.hostname}/form/captcha-fallback?${params}`,
            {
                method: 'GET',
                mode: 'cors',
                credentials: 'omit',
                cache: 'no-cache',
            },
        )
            .then((r) => r.json())
            .then(({ html = '' }) => {
                if (!html) {
                    return;
                }
                this.captchaDiv.innerHTML = html;
                const reloadBtn = this.captchaDiv.querySelector('.btn-cptreload');
                if (reloadBtn) {
                    reloadBtn.addEventListener('click', this.loadFallbackCaptcha.bind(this));
                }
                this.captchaDiv.querySelector('input[type="text"]')?.focus();
            })
            .catch(() => {
                this.captchaDiv.classList.add('hidden');
            });
    }

    /**
     * Validate a single field for if it is set.
     * @param {Object} el DOMElement fields we are validating.
     */
    validateField(el) {
        if (!el || el.disabled) {
            return;
        }
        if (el.tagName === 'SELECT') {
            const option = el.options[el.selectedIndex] || null;
            if (!option) {
                return;
            }
            const value = option.value;
            if (value === '' || value === '0') {
                this.markError(el, this.errormsg.reqfields);
            }
            return;
        }
        if (el.tagName === 'TEXTAREA') {
            if (el.value.trim() === '') {
                this.markError(el, this.errormsg.reqfields);
            }
            return;
        }
        if (el.tagName !== 'INPUT') {
            return;
        }
        // Ok just INPUT tags now.
        const inputType = el.type;
        // Radio and checkbox are handled as groups
        if (inputType === 'radio' || inputType === 'checkbox') {
            return;
        }
        if (inputType === 'email') {
            if (!el.checkValidity()) {
                this.markError(el, this.errormsg.emailaddress);
            }
            if (el.name === 'confirm_pemail') {
                const emailElem = this.form.querySelector('input[name="pemail"]');
                if (el.value.trim() !== emailElem.value.trim()) {
                    this.markError(el, this.errormsg.emailnotmatch);
                }
            }
            return;
        }
        // The rest... text mostly.
        if (el.value.trim() === '') {
            this.markError(el, this.errormsg.reqfields);
        }
    }

    /**
     * Validate a URL field
     * @param {HTMLElement} el element
     */
    validateURL(el) {
        if (!el.reportValidity()) {
            this.markError(el, el.validationMessage);
        }
    };

    /**
     * Validate checkbox and radio groups.
     * @param {HTMLElement} fieldset A fieldset element.
     */
    validateFieldSet(fieldset) {
        const inputs = fieldset.querySelectorAll('input');
        let isValid = true;
        inputs.forEach((input) => {
            if (!input.checkValidity()) {
                isValid = false;
                input.setAttribute('aria-invalid', 'true');
            }
        });
        if (isValid) {
            return;
        }

        const msg_div = document.createElement('div');
        msg_div.classList.add('error-message');
        msg_div.innerHTML = this.errormsg.reqfields;

        fieldset.classList.add('has-error');
        const legend = fieldset.querySelector('legend');
        if (legend) {
            legend.appendChild(msg_div);
            return;
        }
    }

    /**
     * Validate the form before submitting.
     * @return {Boolean}
     */
    validate() {
        if (this.libauth_enabled && !this.libauth_authed) {
            return false;
        }
        this.form.setAttribute('action', this.form_action);
        this.form.querySelectorAll('label .error-message, legend .error-message').forEach((el) => {
            el.remove();
        });
        this.form.querySelectorAll('.form-group').forEach((el) => {
            el.classList.remove('has-error');
        });
        this.form.querySelectorAll('.form-control, input[type=radio], input[type=checkbox]').forEach((el) => {
            el.setAttribute('aria-invalid', false);
        });

        // check "required fields" for validity
        this.form.querySelectorAll('[required]:not([type="url"])').forEach((el) => {
            this.validateField(el);
        });
        // validate URL when it's not empty
        this.form.querySelectorAll('input[type=url]').forEach((el) => {
            this.validateURL(el);
        });
        // check required radio/checkbox groups
        this.form.querySelectorAll('fieldset[data-required]').forEach((el) => {
            this.validateFieldSet(el);
        });

        if (this.form.querySelectorAll('.has-error').length > 0) {
            this.form.querySelector('*[aria-invalid=true]')?.focus();
            return false;
        }
        // @todo this needs fixing later when we put it in public pages
        // let qlog_id = 0;
        // if (window.springSpace?.queryAnalyzer) {
        //     qlog_id = window.springSpace.queryAnalyzer.qlog_id || 0;
        // } else if (window.springSpace?.la?.Page?.qlog_id) {
        //     qlog_id = window.springSpace.la.Page.qlog_id;
        // }
        // if (qlog_id > 0) {
        //     const qLogField = this.form.querySelector('input[name=qlog]');
        //     if (qLogField) {
        //         qLogField.value = qlog_id;
        //     }
        // }
        return true;
    }

    /**
     * Create a dismissable alert for insertion into the DOM.
     * Uses bootstrap alert classes.
     * @param {String} message HTML Error message (html)
     * @param {String} colorClass Bootstrap based (danger, success, info, etc.)
     * @returns {HTMLElement}
     */
    createDismissableAlert(message, colorClass) {
        const alert = document.createElement('div');
        alert.classList.add('alert', `alert-${colorClass}`, 'alert-dismissable');
        alert.setAttribute('role', 'alert');
        alert.setAttribute('aria-atomic', 'true');
        const button = document.createElement('button');
        button.type = 'button';
        button.classList.add('btn-close', 'me-3');
        button.setAttribute('aria-label', 'close');
        alert.appendChild(button);
        alert.insertAdjacentHTML('beforeend', message);
        button.addEventListener('click', (ev) => {
            ev.preventDefault();
            alert.remove();
        });
        return alert;
    }

    /**
     * Fill the name input
     * @param {String} clientname Patron name
     */
    setName(clientname) {
        const nameField = this.form?.querySelector('input[name="pname"]');
        if (nameField) {
            nameField.value = clientname;
        }
    }

    /**
     * Fill the email input
     * @param {String} email Patron email
     */
    setEmail(email) {
        const emailField = this.form?.querySelector('input[name="pemail"]');
        if (emailField) {
            emailField.value = email;
        }
    }

    /**
     * Fill the library card input
     * @param {String} cardNumber Patron email
     */
    setLibraryCard(cardNumber) {
        const cardField = this.form?.querySelector('input[name="localid"]');
        if (cardField) {
            cardField.value = cardNumber;
        }
    }

    resetRules() {
        this.rules.forEach((rule) => {
            this.form.querySelectorAll(`*[name="${rule.field1}"], *[name="${rule.field1}[]"]`).forEach((el) => {
                el.dispatchEvent(new Event('change'));
            });
        });
    }

    /**
     * Instantiate filedrop object to handle validation, submit, error, etc.
     */
    instantiateForm() {
        this.questionform = new FileDrop({
            form: this.form,
            max_files: 5,
            max_upload_size: 20971520,
            max_file_size: 20971520,
            batch_files: false,
            autoupload: false,
            translations: this.translations,
            callback: ({ message = '', errors = [], ticket_id = 0 }) => {
                // Insert an alert above the submit button.
                const alert = this.createDismissableAlert(message, 'success');
                const buttonDiv = this.form.querySelector('#s-la-askform-buttons');
                buttonDiv.insertAdjacentElement('beforebegin', alert);
                this.questionform.reset();
                this.resetRules();
                if (this.captcha_enabled) {
                    this.loadCaptcha();
                }
                if (this.autoreply_enabled && this.confirmEmailDiv) {
                    // remove confirmation field when auto reply is enabled
                    this.confirmEmailDiv.querySelector('input[type="checkbox"]').disabled = true;
                    this.confirmEmailDiv.classList.add('hidden');
                }
                if (this.successCallback) {
                    this.successCallback({ message, errors, ticket_id });
                }
            },
            error_callback: (xhr) => {
                let error = 'Error';
                const isCaptcha = xhr.status === 449;
                if (xhr.responseText) {
                    try {
                        const response = JSON.parse(xhr.responseText);
                        if (response.error) {
                            error = response.error;
                        }
                    } catch {
                        error = 'Invalid response received';
                    }
                }
                const alert = this.createDismissableAlert(error, 'danger');
                let errorContain = null;
                let location = 'beforebegin';
                if (isCaptcha) {
                    errorContain = this.form.querySelector('.formlibcpt');
                    if (errorContain) {
                        location = 'afterbegin';
                    }
                }
                if (!errorContain) {
                    errorContain = this.form.querySelector('#s-la-askform-buttons');
                }
                errorContain.insertAdjacentElement(location, alert);
                if (this.failureCallback) {
                    this.failureCallback();
                }
            },
            validate: this.validate.bind(this),
        });
        this.questionform.init();
    }

    showField(field) {
        field.classList.remove('hidden');
        field.querySelectorAll('select, input, textarea').forEach((input) => {
            input.disabled = false;
        });
    }

    hideField(field) {
        field.classList.add('hidden');
        field.querySelectorAll('select, input, textarea').forEach((input) => {
            if (['checkbox', 'radio'].includes(input.type)) {
                input.checked = false;
            } else {
                input.value = '';
            }
            // trigger change event to hide/show fields...
            input.dispatchEvent(new Event('change'));
            input.disabled = true;
        });
    }

    getMultiCheckValue(fieldname) {
        // get all checkbox values...
        const data = new FormData(this.form);
        return Array.from(data.getAll(fieldname)).map((el) => {
            return Number(el);
        });
    }

    getRadioValue(fieldname) {
        const data = new FormData(this.form);
        return Number(data.get(fieldname));
    }

    triggerInitialRuleEvents() {
        const field1Names = [...new Set(this.rules.map((rule) => rule.field1))];
        field1Names.forEach((fieldName) => {
            const elements = this.form.querySelectorAll(`*[name="${fieldName}"], *[name="${fieldName}[]"]`);
            for (const el of elements) {
                const hasValue = ['checkbox', 'radio'].includes(el.type) ? el.checked : el.value !== '';
                if (hasValue) {
                    el.dispatchEvent(new Event('change'));
                    break; // one dispatch per field name triggers all rule listeners
                }
            }
        });
    }

    setUpRules() {
        this.rules.forEach((rule) => {
            this.form.querySelectorAll(`*[name="${rule.field1}"], *[name="${rule.field1}[]"]`).forEach((el) => {
                const field2 = this.form.querySelector(`.${rule.field2}_wrap`);
                if (!field2) {
                    return;
                }
                // then for later...
                el.addEventListener('change', (ev) => {
                    let fieldValue = Number(ev.target.value);
                    if (ev.target.type === 'checkbox') {
                        // get all checkbox values...
                        fieldValue = this.getMultiCheckValue(ev.target.name);
                    } else if (ev.target.type === 'radio') {
                        fieldValue = this.getRadioValue(ev.target.name);
                    }

                    const action = rule.getFieldAction(fieldValue);
                    if (action === ruleConstants.RULE_ACTION_SHOW) {
                        this.showField(field2);
                    } else if (action === ruleConstants.RULE_ACTION_HIDE) {
                        this.hideField(field2);
                    }
                });
            });
        });
    }

    /**
     * Setup some events on the form.
     * This gets run first.
     * @param {HTMLElement} container Element containing the form
     */
    setUp(container = null) {
        this.container = container ? container : document.querySelector(this.divselector);
        if (!this.container) {
            return;
        }
        this.form = this.container.querySelector('form');
        if (!this.form) {
            return;
        }
        this.captcha_enabled = this.autoReplySettings.isCaptchaEnabled();
        this.autoreply_enabled = this.autoReplySettings.isAutoReplyEnabled();
        this.captchaDiv = this.form.querySelector('.formlibcpt');
        this.confirmEmailDiv = this.form.querySelector('.confem_wrap');
        this.autoreply_type = this.autoReplySettings.getAutoReplyType();
        if (this.captcha_enabled) {
            this.captchaDiv.classList.remove('hidden');
        }

        this.instantiateForm();

        // LibAuth on but current user not authed
        if (this.libauth_enabled && !this.libauth_authed) {
            // Disable inputs to stop focus
            this.form.querySelectorAll('input,select,button,textarea').forEach((el) => {
                el.disabled = true;
            });
            // make tabbable elements and links untabbable.
            this.form.querySelectorAll('[tabindex="0"],a').forEach((el) => {
                el.setAttribute('tabindex', '-1');
            });
            // hide form for screenreaders
            this.form.setAttribute('aria-hidden', 'true');
            // If in iframe set auth link to open in new window.
            if (window !== window.top) {
                const button = this.container.querySelector('.btn-libauth');
                if (button) {
                    button.setAttribute('target', '_blank');
                }
            }
            return;
        }

        this.setUpRules();
        this.triggerInitialRuleEvents();

        const reqChecks = this.form.querySelectorAll('input[type=checkbox][required]');
        if (reqChecks.length > 0) {
            // Add change event for all required checkboxes.
            reqChecks.forEach((el) => {
                el.addEventListener('change', this.handleRequiredCheckBoxes.bind(this));
            });
        }
        if (this.captcha_enabled) {
            this.loadCaptcha();
        }
        if (this.autoreply_enabled && this.confirmEmailDiv) {
            // remove confirmation field when auto reply is enabled
            this.confirmEmailDiv.querySelector('input[type="checkbox"]').disabled = true;
            this.confirmEmailDiv.classList.add('hidden');
        }
        if (!this.captcha_enabled && this.confirmEmailDiv) {
            const confemCheckbox = this.confirmEmailDiv.querySelector('input[type="checkbox"]');
            if (confemCheckbox) {
                confemCheckbox.addEventListener('change', () => {
                    if (confemCheckbox.checked && !this.captcha_enabled) {
                        this.captcha_enabled = true;
                        this.loadCaptcha();
                    }
                });
            }
        }
    }
}

export {
    FormAutoReply,
    QuestionForm,
    Pbkdf2Worker,
};
